import React, { useMemo, useState } from "react";
import { Form, Formik, FormikConfig } from "formik";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { Organization } from "@common/types/organization";
import { useWorkspacesList } from "@api/workspace.api";
import { getPossessive } from "@common/helpers/string";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { submitHelper } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Button } from "@design-system/Button/Button";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { useUpdateOrganizationMemberPermissionsMutation } from "@api/organizationMember.api";
import styles from "../../memberManagement/MemberManagement.module.scss";
import { OrganizationMemberPermissionsSchema } from "./types";
import { ConfigureOrganizationPermissionsFields } from "./ConfigureOrganizationPermissionsFields";

interface Props {
    member: OrganizationMember;
    organization: Organization;
    closeModal: () => void;
}

export const ConfigureOrganizationPermissionsModal: React.FC<Props> = ({
    member,
    organization,
    closeModal,
}) => {
    const { toast } = useToaster();
    const [loading, setLoading] = useState(false);

    const workspacesQuery = useWorkspacesList();

    const workspaces = useMemo(
        () => workspacesQuery.data ?? [],
        [workspacesQuery.data],
    );
    const updateMemberPermissions =
        useUpdateOrganizationMemberPermissionsMutation(member, organization);

    const form: FormikConfig<OrganizationMemberPermissionsSchema> = {
        initialValues: {
            organizationRole: member.role,
            workspaceAssignments: workspaces.reduce(
                (acc, workspace) => {
                    acc[workspace.id] = member.workspaceAssignments.some(
                        (assignment) => assignment.workspaceId === workspace.id,
                    );
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
            assignToNewClients: member.assignToNewClients,
        },
        validate({ workspaceAssignments }) {
            if (!Object.values(workspaceAssignments).some((wa) => wa)) {
                return {
                    workspaceAssignments:
                        "At least one workspace must be selected",
                };
            }
        },
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({
                organizationRole,
                workspaceAssignments,
                assignToNewClients,
            }) => {
                await updateMemberPermissions.mutateAsync({
                    role: organizationRole,
                    workspaceAssignments: Object.keys(
                        workspaceAssignments,
                    ).filter(
                        (workspaceId) => workspaceAssignments[workspaceId],
                    ),
                    assignToNewClients,
                });

                closeModal();
                toast(`User permissions updated`);
            },
        }),
    };

    return (
        <>
            <StandardModalHeader>
                Configure{" "}
                {member.user.name ? getPossessive(member.user.name) : null}{" "}
                permissions
            </StandardModalHeader>

            <section className={styles.layout}>
                <Formik {...form}>
                    {({ isValid }) => (
                        <Form>
                            <main className={styles.teamForm}>
                                <ConfigureOrganizationPermissionsFields
                                    workspaces={workspaces}
                                    organization={organization}
                                />

                                <footer className={styles.footer}>
                                    <Button
                                        variant="secondary"
                                        size="xl"
                                        onClick={closeModal}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                    <ButtonWithLoader
                                        loading={loading}
                                        size="xl"
                                        type="submit"
                                        disabled={!isValid}
                                        variant="primary"
                                    >
                                        Update permissions
                                    </ButtonWithLoader>
                                </footer>
                            </main>
                        </Form>
                    )}
                </Formik>
            </section>
        </>
    );
};
